import React, { useEffect, useState } from 'react';
import { ILineAmendmentLovs, ILineAmendmentProps } from '.';
import {
  convertToPolicyPlanProposalCovers,
  mapToAmendmentDetails,
} from '../utils';
import { useLazyQuery } from '@apollo/client';
import { getCoversPersonsQuery, getExpatDetailsQuery } from '../queries';
import { cloneDeep } from 'lodash';
import { initialCoverValues } from '../components/content';
import Loader from '@components/loader/Loader';
import AmendmentDetails from '../components/AmendmentDetails';
import PolicyCovers from '../components/PolicyCovers';
import CostsAndCharges from '../components/CostsAndCharges';
import ExpatDetailsWidget from '../components/ExpatDetails';
import { getAmendmentInitialState } from '../content';
import InsuredWidget from '../components/policy-persons-widgets/InsuredWidget';
import PayerOwnerWidget from '../components/policy-persons-widgets/PayerOwnerWidget';
import BeneficiaryWidget from '../components/policy-persons-widgets/BeneficiaryWidget';
import { convertToPolicyPersons } from '../components/policy-persons-widgets/utils';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';
import AmendmentPolicyDetails from '../components/AmendmentPolicyDetails';

const ExpatAmendment: React.FC<ILineAmendmentProps> = ({
  generalData,
  data,
  pageState,
  handleStateChange,
}) => {
  const amendmentData = data?.Production?.queries?.getAmendmentDetails;
  const [loading, setLoading] = useState<boolean>();
  const isDisabled = ['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(
    generalData.amendment.status
  );

  const [lovs, setLovs] = useState<ILineAmendmentLovs>({
    amendmentTypes: {},
    amendmentSubTypes: {},
  });

  const [getCoversPersonsLazy] = useLazyQuery(getCoversPersonsQuery('both'));

  const [getExpatDetailsLazy] = useLazyQuery(getExpatDetailsQuery, {
    variables: {
      selectedAmendmentID: generalData.amendment.id,
      version: generalData.policy.version,
    },
    errorPolicy: 'all',
  });

  const initialize = async () => {
    setLoading(true);

    const { data: detailsData } = await getExpatDetailsLazy();

    const newValues = mapToAmendmentDetails({
      ...amendmentData?.[0],
      ...detailsData.Production.queries.getAmendmentDetails[0],
    });
    const newErrors = cloneDeep(
      getAmendmentInitialState(generalData.line.category).errors
    );
    const newTouched = cloneDeep(
      getAmendmentInitialState(generalData.line.category).touched
    );

    // get covers and/or persons --start
    const { data: coversPersonsData } = await getCoversPersonsLazy({
      variables: {
        id: generalData.proposal.id,
        version: generalData.policy.version,
      },
      errorPolicy: 'all',
    });

    const covers = convertToPolicyPlanProposalCovers(
      coversPersonsData,
      amendmentData
    );
    newValues.covers = covers;
    newErrors.covers = covers.map(() => initialCoverValues.initialError);
    newTouched.covers = covers.map(() => initialCoverValues.initialTouched);

    const persons = convertToPolicyPersons(coversPersonsData);
    newValues.policyPersons = persons;
    newErrors.policyPersons = {
      owner: newErrors['policyPersons'].owner,
      payer: newErrors['policyPersons'].payer,
      insured: persons.insured.map(() => newErrors['policyPersons'].insured[0]),
      beneficiaries: persons.beneficiaries.map(
        () => newErrors['policyPersons'].beneficiaries[0]
      ),
    };
    newTouched.policyPersons = {
      owner: newTouched['policyPersons'].owner,
      payer: newTouched['policyPersons'].payer,
      insured: persons.insured.map(
        () => newTouched['policyPersons'].insured[0]
      ),
      beneficiaries: persons.beneficiaries.map(
        () => newTouched['policyPersons'].beneficiaries[0]
      ),
    };
    // get covers and/or persons --end

    setLovs({
      amendmentTypes: enumListAsRecordObject(
        data.Production_AmendmentTypes.enumValues
      ),
      amendmentSubTypes: lookupListAsRecordObject(
        data.Production.lookups.amendmentSubTypes,
        false,
        'Code',
        'Title'
      ),
    });

    handleStateChange({
      values: newValues,
      errors: newErrors,
      touched: newTouched,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (data) {
      initialize();
    }
  }, [data]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <AmendmentDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
      />
      <AmendmentPolicyDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
      />
      <PolicyCovers
        generalData={generalData}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
      />

      <CostsAndCharges
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
      <InsuredWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
        maxNumber={
          amendmentData?.[0]?.policy_PlanID?.views?.PlanConfigManagement_all
            ?.properties?.MaxNbInsured
        }
      />
      <PayerOwnerWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
      <BeneficiaryWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
        maxNumber={
          amendmentData?.[0]?.policy_PlanID?.views?.PlanConfigManagement_all
            ?.properties?.MaxNbBeneficiary
        }
      />
      <ExpatDetailsWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
    </>
  );
};

export default ExpatAmendment;
